import { Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { TranscriptTypes } from '../../constants/enums';
import {
  selectEventAPI,
  selectEventAPIAuth,
  selectFeatureFlagHitl3,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import ConfirmDialog from '../common/ConfirmDialog';
import asrPromotedIcon from '../../images/asr-promoted.svg';

import {
  BlockedReason,
  EntitySpan,
  EntityType,
} from '../../reducers/messagingSlice';

type TranscriptMessageStyleProps = {
  type: TranscriptTypes;
  isBlocked?: boolean;
  blockedReason?: BlockedReason;
  // HITL 3.0 transcript card uses a denser layout (smaller font/spacing); the
  // OLD transcript leaves this false for the original 14px/4px look.
  compact?: boolean;
  // hitl_3_0 flag: drives the shared hitl3 message-bubble font size. Kept
  // separate from `compact` so only the flagged UI is affected, never legacy.
  isHitl3?: boolean;
};

const getBlockedColor = (blockedReason?: BlockedReason): string => {
  if (blockedReason === BlockedReason.debounce) {
    return Colors.blockedDebounce;
  }
  if (blockedReason === BlockedReason.pendingAsr) {
    return Colors.blockedPendingAsr;
  }
  return Colors.blockedDefault;
};

const getBlockedTooltipText = (blockedReason?: BlockedReason): string => {
  if (blockedReason) {
    return `Blocked: ${blockedReason}`;
  }
  return 'Blocked';
};

// Compact (HITL 3.0) bubbles show a WhatsApp-style clock at the bottom-right,
// derived from the message's ISO `timestamp`. 12-hour with am/pm, locale-forced
// so it reads "3:45 PM" regardless of the agent's locale. When hitl_3_0 is on it
// includes seconds ("3:45:07 PM"). Returns '' for a missing/invalid timestamp so
// nothing renders.
const formatClockTime = (timestamp?: string, withSeconds = false): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  if (withSeconds) options.second = '2-digit';
  return date.toLocaleTimeString('en-US', options);
};

const ENTITY_HIGHLIGHT_COLORS: Record<
  EntityType,
  { background: string; color: string }
> = {
  menu_item: { background: '#BBDEFB', color: '#1565C0' },
  quantity: { background: '#C8E6C9', color: '#2E7D32' },
  coupon: { background: '#FFE0B2', color: '#E65100' },
  discount: { background: '#F8BBD0', color: '#AD1457' },
  loyalty: { background: '#E1BEE7', color: '#6A1B9A' },
  size: { background: '#C5CAE9', color: '#283593' },
};

const useStyles = makeStyles<Theme, TranscriptMessageStyleProps>(({}) => ({
  transcriptMessageClass: {
    fontSize: ({ compact }) => (compact ? '12px' : '14px'),
    lineHeight: '20px',
    paddingBottom: ({ compact }) => (compact ? '2px' : '4px'),
    display: 'flex',
    color: ({ type, isBlocked, blockedReason }) =>
      isBlocked
        ? getBlockedColor(blockedReason)
        : type === TranscriptTypes.AI
        ? '#1E1E1E'
        : Colors.guardsmanRed,
    textDecoration: ({ isBlocked }) => (isBlocked ? 'line-through' : 'none'),
    textDecorationThickness: ({ isBlocked }) => (isBlocked ? '3px' : 'auto'),
    fontWeight: ({ isBlocked }) => (isBlocked ? 'bold' : 'normal'),
  },
  sourceClass: {
    // HITL 3.0 (compact): no fixed width — the source label sizes to its text
    // and the content flows naturally. Legacy keeps the original fixed widths
    // (the calc below also accounted for the language indicator, which compact
    // doesn't render).
    width: ({ compact }) => (compact ? undefined : '1.75rem'),
    fontWeight: 'bold',
    textDecoration: 'inherit',
  },
  contentClass: {
    width: ({ compact }) => (compact ? undefined : 'calc(100% - 3.75rem)'),
    textDecoration: 'inherit',
  },
  wordSpanClass: {
    display: 'inline-block',
    padding: '0 2px',
    borderRadius: '3px',
    textDecoration: 'inherit',
    '&:hover': {
      backgroundColor: '#fff59d',
      cursor: 'pointer',
    },
  },
  langIndicatorClass: {
    width: '2rem',
    textAlign: 'center',
    paddingRight: '4px',
  },
  secondLangIndicatorClass: {
    backgroundColor: '#0797EA',
    color: '#FFFFFF',
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  secondLangClass: {
    fontStyle: 'italic',
  },
  entityHighlightClass: {
    borderRadius: '3px',
    padding: '0 2px',
    fontWeight: 600,
  },
  // secondary_asr: "promoted" badge sizing. width:auto keeps the SVG's aspect
  // ratio; the badge sits after the message content (before the timestamp in the
  // compact bubble) on the text's baseline. marginLeft separates it from the
  // preceding text. Slightly smaller in the compact bubble than in legacy.
  promotedIconClass: {
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    height: '16px',
    width: 'auto',
    marginLeft: '4px',
  },
  promotedIconCompactClass: {
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    height: '14px',
    width: 'auto',
    marginLeft: '3px',
  },
  // --- Compact (HITL 3.0 card) only: conversational chat bubbles. AI is
  // right-aligned, the customer (CX) left-aligned, so the two speakers read as
  // a back-and-forth. Legacy (non-compact) transcript is untouched.
  bubbleRow: {
    display: 'flex',
    width: '100%',
    marginBottom: '6px',
  },
  bubbleRowAI: {
    justifyContent: 'flex-end',
  },
  bubbleRowCX: {
    justifyContent: 'flex-start',
  },
  bubble: {
    position: 'relative',
    maxWidth: '100%',
    padding: '6px 10px',
    borderRadius: '12px',
    // hitl_3_0 uses the shared hitl3 size; the compact-but-flag-off bubble keeps
    // its original 12px so only the flagged UI grows.
    fontSize: ({ isHitl3 }) => (isHitl3 ? HITL3_FONT_SIZE : '12px'),
    lineHeight: '17px',
    wordBreak: 'break-word',
  },
  // AI bubble: pale green tint of the "Order Flow" intent-group color
  // (Colors.welcome @ ~12%), with a WhatsApp-style tail at the TOP-RIGHT — the
  // corner is squared and a small triangle (same tint) flares outward from it.
  bubbleAI: {
    backgroundColor: `${Colors.welcome}1F`,
    color: '#1E1E1E',
    borderTopRightRadius: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -6,
      width: 6,
      height: 8,
      backgroundColor: `${Colors.welcome}1F`,
      clipPath: 'polygon(0 0, 100% 0, 0 100%)',
    },
  },
  // CX bubble: pale blue tint of the "Conversation" intent-group color
  // (Colors.readBack @ ~12%), mirrored tail at the TOP-LEFT.
  bubbleCX: {
    backgroundColor: `${Colors.readBack}1F`,
    color: '#1E1E1E',
    borderTopLeftRadius: 0,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: -6,
      width: 6,
      height: 8,
      backgroundColor: `${Colors.readBack}1F`,
      clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
    },
  },
  // WhatsApp-style timestamp (hitl_3_0 OFF): stacked on its own line at the
  // bottom-right of the bubble.
  bubbleTime: {
    display: 'block',
    textAlign: 'right',
    fontSize: '10px',
    lineHeight: 1,
    marginTop: 3,
    color: 'rgba(0, 0, 0, 0.45)',
    fontVariantNumeric: 'tabular-nums',
  },
  // hitl_3_0 ON: lay the bubble out as a row so the message text and the
  // timestamp share the same line — text on the left, stamp on the right —
  // instead of the stamp dropping to its own line. `last baseline` sits the
  // smaller stamp on the text's baseline (bottom line when the text wraps), so
  // it lines up with the words rather than floating slightly high.
  bubbleFlexHitl3: {
    display: 'flex',
    alignItems: 'last baseline',
    gap: 8,
  },
  // hitl_3_0 ON: the message text is the growing flex child; min-width:0 lets it
  // wrap within the row instead of shoving the timestamp off it.
  bubbleContentFlex: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  // hitl_3_0 ON: the timestamp rides at the right end of the text, in a distinct
  // monospace face + muted tone so it reads as metadata. flexShrink:0 so it keeps
  // its width and the text wraps around it rather than the stamp being squeezed.
  bubbleTimeHitl3: {
    flexShrink: 0,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: '10px',
    lineHeight: '17px',
    letterSpacing: '0.02em',
    color: 'rgba(0, 0, 0, 0.45)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
}));

interface ITranscriptMessage {
  text: string;
  type: TranscriptTypes;
  secondLangText?: string;
  isBlocked?: boolean;
  blockedReason?: BlockedReason;
  entityAnnotations?: EntitySpan[];
  // secondary_asr: when true, render a "promoted" badge before the text.
  // Already gated on the secondary-ASR flags (see config.flags) by the parent
  // (MessageContainer), so this is presentational only.
  isPromoted?: boolean;
  compact?: boolean;
  // ISO timestamp of the message; rendered as a WhatsApp-style clock in the
  // compact bubble. Ignored by the legacy layout.
  timestamp?: string;
}

export const TranscriptMessage = ({
  text = '•',
  type,
  secondLangText = '',
  isBlocked = false,
  blockedReason,
  entityAnnotations,
  isPromoted = false,
  compact = false,
  timestamp,
}: ITranscriptMessage) => {
  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const dispatch = useAppDispatch();
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const {
    transcriptMessageClass,
    sourceClass,
    contentClass,
    langIndicatorClass,
    secondLangClass,
    secondLangIndicatorClass,
    wordSpanClass,
    entityHighlightClass,
    bubbleRow,
    bubbleRowAI,
    bubbleRowCX,
    bubble,
    bubbleAI,
    bubbleCX,
    bubbleTime,
    bubbleTimeHitl3,
    bubbleContentFlex,
    bubbleFlexHitl3,
    promotedIconClass,
    promotedIconCompactClass,
  } = useStyles({
    type,
    isBlocked,
    blockedReason,
    compact,
    isHitl3,
  });

  const lastClickRef = useRef<{ word: string; time: number } | null>(null);

  const words = useMemo(() => {
    if (!text) {
      return [] as string[];
    }
    return text.split(/\s+/).filter((w) => w.length > 0);
  }, [text]);

  const annotatedSegments = useMemo(() => {
    if (!entityAnnotations?.length || !text) return null;
    const sorted = [...entityAnnotations].sort((a, b) => a.start - b.start);
    const segments: { text: string; entity?: EntitySpan }[] = [];
    let cursor = 0;
    for (const span of sorted) {
      if (
        span.start < cursor ||
        span.end <= span.start ||
        span.end > text.length
      )
        continue;
      if (span.start > cursor) {
        segments.push({ text: text.slice(cursor, span.start) });
      }
      segments.push({
        text: text.slice(span.start, span.end),
        entity: span,
      });
      cursor = span.end;
    }
    if (cursor < text.length) {
      segments.push({ text: text.slice(cursor) });
    }
    return segments;
  }, [text, entityAnnotations]);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    word: string;
  }>({ open: false, word: '' });

  const openConfirmDialog = useCallback(
    (wordClickedRaw: string | undefined) => {
      const wordClicked =
        !wordClickedRaw || wordClickedRaw === '/silence'
          ? 'ASR MISSING'
          : wordClickedRaw;

      const now = Date.now();
      if (
        lastClickRef.current &&
        lastClickRef.current.word === wordClicked &&
        now - lastClickRef.current.time < 500
      ) {
        return;
      }
      lastClickRef.current = { word: wordClicked, time: now };

      setConfirmDialog({ open: true, word: wordClicked });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    const wordClicked = confirmDialog.word;

    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked,
      dispatch,
    });

    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, [
    confirmDialog.word,
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  const handleCloseDialog = useCallback(() => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const handleContentClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      const target = event.target as HTMLElement | null;
      const wordFromTarget = target?.dataset?.word;
      if (!wordFromTarget) {
        return;
      }

      openConfirmDialog(wordFromTarget);
    },
    [openConfirmDialog]
  );

  const contentInner = (
    <>
      {annotatedSegments
        ? annotatedSegments.map((seg, i) => {
            const segWords = seg.text.split(/\s+/).filter((w) => w.length > 0);
            const wordSpans = segWords.map((word, wi) => (
              <span
                key={`${word}-${i}-${wi}`}
                data-word={word}
                className={wordSpanClass}
              >
                {wi > 0 ? ' ' : ''}
                {word}
              </span>
            ));
            if (seg.entity) {
              return (
                <Tooltip
                  key={`entity-${i}`}
                  title={seg.entity.type.replaceAll('_', ' ')}
                  placement="top"
                >
                  <span
                    className={entityHighlightClass}
                    style={{
                      backgroundColor:
                        ENTITY_HIGHLIGHT_COLORS[seg.entity.type]?.background ??
                        '#E0E0E0',
                      color: ENTITY_HIGHLIGHT_COLORS[seg.entity.type]?.color,
                    }}
                  >
                    {wordSpans}
                  </span>
                </Tooltip>
              );
            }
            return (
              <React.Fragment key={`gap-${i}`}>{wordSpans}</React.Fragment>
            );
          })
        : words.length === 0
        ? text
        : words.map((word, index) => (
            <span
              key={`${word}-${index}`}
              data-word={word}
              className={wordSpanClass}
            >
              {index > 0 ? ' ' : ''}
              {word}
            </span>
          ))}
      {secondLangText ? (
        <span className={secondLangClass}>{`     | ${secondLangText}`}</span>
      ) : null}
    </>
  );

  const isAI = type === TranscriptTypes.AI;
  // HITL 3.0 shows seconds (hh:mm:ss); legacy stays hh:mm.
  const timeLabel = formatClockTime(timestamp, isHitl3);

  // secondary_asr: small waveform+arrow badge marking a CX line whose text was
  // promoted (higher-confidence) by ASR. Rendered inline before the text in both
  // the compact bubble and the legacy layout. `isPromoted` is already gated on
  // the secondary-ASR flags by MessageContainer.
  const promotedIcon = isPromoted ? (
    <Tooltip
      title="Promoted by secondary ASR (higher confidence)"
      placement="top"
    >
      <img
        src={asrPromotedIcon}
        alt="ASR promoted"
        className={compact ? promotedIconCompactClass : promotedIconClass}
      />
    </Tooltip>
  ) : null;

  // Compact card: a chat bubble aligned by speaker. Legacy layout otherwise.
  // The blocked (strike-through) styling sits on the content — not the whole
  // bubble — so the timestamp below it stays legible.
  const bubbleMarkup = (
    <div className={`${bubbleRow} ${isAI ? bubbleRowAI : bubbleRowCX}`}>
      <div
        className={`${bubble} ${isAI ? bubbleAI : bubbleCX} ${
          isHitl3 ? bubbleFlexHitl3 : ''
        }`}
      >
        <div
          className={
            isHitl3 ? `${contentClass} ${bubbleContentFlex}` : contentClass
          }
          onClick={handleContentClick}
          style={
            isBlocked
              ? {
                  textDecoration: 'line-through',
                  textDecorationThickness: '3px',
                  color: getBlockedColor(blockedReason),
                  fontWeight: 'bold',
                }
              : undefined
          }
        >
          {contentInner}
        </div>
        {promotedIcon}
        {timeLabel ? (
          <span className={isHitl3 ? bubbleTimeHitl3 : bubbleTime}>
            {timeLabel}
          </span>
        ) : null}
      </div>
    </div>
  );

  const messageContent = (
    <div className={transcriptMessageClass}>
      {!compact && (
        <div className={langIndicatorClass}>
          <div className={secondLangIndicatorClass}>
            {secondLangText ? 'S' : ''}
          </div>
        </div>
      )}
      <div className={sourceClass}>{type}:&nbsp;</div>
      <div className={contentClass} onClick={handleContentClick}>
        {contentInner}
      </div>
      {promotedIcon}
    </div>
  );

  const rendered = compact ? bubbleMarkup : messageContent;

  return (
    <>
      {isBlocked ? (
        <Tooltip title={getBlockedTooltipText(blockedReason)} placement="top">
          {rendered}
        </Tooltip>
      ) : (
        rendered
      )}
      <ConfirmDialog
        // Portal-rendered, so it needs the transcript marker itself — confirming
        // a mis-heard word is a report, not a take-over of the order.
        data-transcript="true"
        open={confirmDialog.open}
        content={`Send ASR incorrect event for word: "${
          confirmDialog.word || 'ASR MISSING'
        }"?`}
        onClose={handleCloseDialog}
        onConfirm={handleConfirm}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </>
  );
};
